import _ from 'lodash';

import {
  CHECKLIST_INITIAL_STATUSES,
  CheckItemResultType,
  ChecklistItemDeterminationType,
  ChecklistItemResultValueType,
  ChecklistPendingChangeStatusType,
} from 'constants/checklists';
import { EMPTY_GUID } from 'constants/data';
import { ChecklistOverrideType } from 'constants/loanApplication';

/**
 * FH-1931: reshapes the FH-1829 flat-store `FlatChecklistResult[]` (`POST api/checklist-results/v2`
 * on Loans, `GET api/sub-companies/{id}/checklist-result/v2` on Accounts) into the EXACT legacy
 * wire shapes the loan-editor and sub-company checklist selectors already consume — so the
 * flat-store read is a drop-in swap behind a feature flag with zero downstream selector/reducer
 * changes.
 *
 * The flat item carries only the LATEST override state (`wasOverriden`/`overridenBy`/`overrideDate`/
 * `pendingChangeStatus`), not the legacy `overrideHistory.entries[]` audit trail (a list of every past
 * override/remove-override). Every selector wired to the flat store only checks for the EXISTENCE of
 * a current (applied or pending) override, never walks history beyond the latest entry, so a single
 * synthetic entry reproduces their behavior exactly; a consumer that needs the full historical trail
 * (the Alerts suppressed-fields list) is intentionally NOT wired to the flat store yet — see FH-1931 PR.
 */

const isPendingOverride = pendingChangeStatus =>
  pendingChangeStatus?.type === ChecklistPendingChangeStatusType.READY_TO_APPLY &&
  pendingChangeStatus?.change?.type === ChecklistOverrideType.OVERRIDE;

// FH-3004: Loans resolves the attribution display names BE-side (`overridenByName`/
// `pendingChangeByName` on `FlatChecklistItem`) — carried onto each history entry's `change` as
// `byUserName` alongside the existing `byUserId`, so a flag-ON consumer never needs its own
// user-id-to-name lookup. `byUserName` is `undefined` here for anything built off the legacy
// (non-flat) shape, which carries no such field — callers fall back to their own user lookup.
const buildOverrideHistory = ({
  wasOverriden,
  overridenBy,
  overridenByName,
  overrideDate,
  pendingChangeStatus,
  pendingChangeByName,
}) => {
  if (wasOverriden) {
    return {
      wasOverriden,
      pendingChangeStatus,
      entries: [
        {
          change: {
            type: ChecklistOverrideType.OVERRIDE,
            byUserId: overridenBy,
            byUserName: overridenByName,
            createdDate: overrideDate,
          },
        },
      ],
    };
  }

  if (isPendingOverride(pendingChangeStatus)) {
    return {
      wasOverriden,
      pendingChangeStatus,
      entries: [{ change: { ...pendingChangeStatus.change, byUserName: pendingChangeByName } }],
    };
  }

  return { wasOverriden, pendingChangeStatus, entries: [] };
};

// FH-2728: `FlatChecklistItem.ResultType` (`CheckItemResultType`: YesOrNo/Value/Manual) carries the
// item's result kind on the wire. Two legacy discriminators derive from it, in two different string
// formats, read by two different places:
//  - the skeleton item's `expressionResult` (ChecklistItemDeterminationType: True/False/Value/Manual/
//    NotApplicable/NeedsReview) — `getActionParams` branches on it for the Override/Remove-Override
//    vs Complete/Reset button. `toDefinitionNode` takes it from the flat item's `determination` and
//    uses this ResultType mapping only for blobs that carry none, so it can't by itself produce a
//    NotApplicable/NeedsReview state row.
//  - the RESULT item's `type` (ChecklistItemResultValueType: the long polymorphic-union name) —
//    `ChecklistItemInfo` derives `isValue` from it together with the definition's state-row check
//    (a state row is stored Value-shaped) for override-info visibility, and `isReviewable` for the
//    field-linked "Create Task" primary-action gate.
// YesOrNo collapses legacy's separate True/False cases — both share the exact same `getActionParams`
// branch, so which one is picked here is immaterial.
const CHECKLIST_ITEM_DETERMINATION_TYPE_BY_RESULT_TYPE = {
  [CheckItemResultType.YES_OR_NO]: ChecklistItemDeterminationType.TRUE,
  [CheckItemResultType.VALUE]: ChecklistItemDeterminationType.VALUE,
  [CheckItemResultType.MANUAL]: ChecklistItemDeterminationType.MANUAL,
};

const CHECKLIST_ITEM_RESULT_VALUE_TYPE_BY_RESULT_TYPE = {
  [CheckItemResultType.YES_OR_NO]: ChecklistItemResultValueType.YES_NO,
  [CheckItemResultType.VALUE]: ChecklistItemResultValueType.VALUE,
  [CheckItemResultType.MANUAL]: ChecklistItemResultValueType.MANUAL,
};

// One flat item -> the legacy RESULT item shape (`items[]`/`itemsMap` across both tabs). `expressionResult`
// here is renamed from the flat item's `valueExpressionResult` to match the legacy RESULT item's field
// name — the formatted VALUE text for a Value-type item, or the reason for a Not Applicable / Needs
// Review state row (`formatChecklistValue`), not the item's True/False/Value/Manual/NotApplicable/
// NeedsReview determination TYPE (see `toDefinitionNode` below for that one).
const toResultItem = item => ({
  checklistItemId: item.checklistItemId,
  id: item.checklistItemId,
  name: item.name,
  orderSequence: item.orderSequence,
  parentId: item.parentId ?? null,
  result: item.result,
  resultWithoutPendingOverrides: item.resultWithoutPendingOverrides,
  fieldRef: item.fieldRef,
  valueFormat: item.valueFormat,
  taskTypeId: item.taskTypeId,
  createTaskButtonLabel: item.createTaskButtonLabel,
  wasOverriden: item.wasOverriden,
  overridenBy: item.overridenBy,
  overridenByName: item.overridenByName,
  overrideDate: item.overrideDate,
  expressionResult: item.valueExpressionResult,
  visibilityExpressionResult: item.visibilityExpressionResult,
  pendingChangeStatus: item.pendingChangeStatus,
  pendingChangeByName: item.pendingChangeByName,
  overrideHistory: buildOverrideHistory(item),
  type: CHECKLIST_ITEM_RESULT_VALUE_TYPE_BY_RESULT_TYPE[item.resultType],
  state: item.state,
});

// One flat item -> the legacy DEFINITION/skeleton item shape (`checklist.categories[].items[]`).
// `checklistId`/`categoryId` are passed in by the per-category caller (the flat item carries no
// back-reference to its own category) — `ChecklistItemInfo` reads `item.checklistId` at runtime
// (the borrower-scoping lookup + the override/task dispatch body), not just as a PropTypes shape.
const toDefinitionNode = (item, { checklistId, categoryId } = {}) => ({
  id: item.checklistItemId,
  checklistItemId: item.checklistItemId,
  checklistId,
  categoryId,
  name: item.name,
  orderSequence: item.orderSequence,
  // FH-1931: `makeTree` (Tree.utils.js) groups by `parentId` and reads the root bucket back via
  // the STRING key `'null'` (`_.get(groupedByParents, 'null', [])`) — `_.groupBy` stringifies a
  // JS `null` to `'null'` but an `undefined` to `'undefined'`. The flat API omits `parentId`
  // entirely on a top-level item (no key at all, not `parentId: null`), so reading it off raw
  // produced `undefined` here and every category's tree silently came back empty (the Alerts /
  // sub-company checklist detail table rendered "No results" despite fully populated data).
  parentId: item.parentId ?? null,
  fieldRef: item.fieldRef,
  valueFormat: item.valueFormat,
  taskTypeId: item.taskTypeId,
  createTaskButtonLabel: item.createTaskButtonLabel,
  hideOnLoanSummary: !!item.hideOnLoanSummary,
  // A Not Applicable / Needs Review state row is stored Value-shaped, so only the carried
  // `determination` tells it apart; blobs written before it was stored fall back to `resultType`.
  expressionResult:
    item.determination ?? CHECKLIST_ITEM_DETERMINATION_TYPE_BY_RESULT_TYPE[item.resultType],
});

// Walks a flat item tree (each node's `children[]`) into ONE flat array, mapping every node
// (root + every descendant) through `mapNode` — mirrors how the legacy payload already carries a
// category's items pre-flattened (parent and child items as siblings, tagged by `parentId`) for
// `makeTree`/`itemsMap`-style client-side re-nesting.
const flattenItemTree = (items, mapNode) =>
  (items ?? []).reduce((flat, item) => {
    flat.push(mapNode(item));

    if (item.children?.length) {
      flat.push(...flattenItemTree(item.children, mapNode));
    }

    return flat;
  }, []);

// One flat category -> the legacy RESULT category shape (the flat, non-nested list the Alerts
// AND sub-company tabs both key by `checklistCategoryId` to drive the `category.result !==
// resultWithoutPendingOverrides` pending-change marker).
const toResultCategory = category => ({
  checklistCategoryId: category.checklistCategoryId,
  result: category.result,
  resultWithoutPendingOverrides: category.resultWithoutPendingOverrides,
  state: category.state,
});

// One flat category -> the legacy DEFINITION/skeleton category shape (`checklist.categories[]`),
// with its items flattened (parent + every descendant as siblings, tagged by `parentId`) exactly
// like the legacy payload, so `makeTree`/`itemsMap` client-side re-nesting is unaffected.
const toDefinitionCategory = (category, checklistId) => ({
  id: category.checklistCategoryId,
  checklistCategoryId: category.checklistCategoryId,
  name: category.name,
  orderSequence: category.orderSequence,
  isRoot: category.isRoot,
  items: flattenItemTree(category.items, item =>
    toDefinitionNode(item, { checklistId, categoryId: category.checklistCategoryId }),
  ),
});

// Shared by both lane-shapes: the `status.result`/top-level RESULT half (calculatedResult,
// categories, items) plus the `checklist` DEFINITION half (name/isHidden/displaySettings +
// the nested skeleton tree) — identical for the loan snapshot and the sub-company result, which
// differ only in how this gets wrapped (see the two mappers below).
const toChecklistResultFields = flatResult => {
  const sortedCategories = _.sortBy(flatResult.categories, 'orderSequence');

  return {
    calculatedResult: flatResult.calculatedResult,
    calculatedState: flatResult.calculatedState,
    createdDate: flatResult.createdDate,
    documentId: flatResult.documentId,
    categories: sortedCategories.map(toResultCategory),
    items: flattenItemTree(
      sortedCategories.flatMap(category => category.items),
      toResultItem,
    ),
    checklist: {
      ...toChecklistDefinitionHead(flatResult),
      categories: sortedCategories.map(category =>
        toDefinitionCategory(category, flatResult.targetChecklistId),
      ),
    },
  };
};

// One `FlatChecklistResult` (one lane) -> one legacy `ChecklistExecutionSnapshotModel`-shaped
// entry, exactly what `normalize(data, [loanApplicationChecklistExecutionSnapshotsById])`
// (the mount read) and `normalize(data, [checklistSchema])` (the per-checklist FULL read) already
// expect — so neither downstream normalize/reducer/selector changes when the flag flips.
// LEN-313: Loans returns a checklist still on its first calculation (InitialInProgress) or whose first
// calculation failed (InitialStale) as a head-only row with the empty result id. The legacy snapshot
// carried no `status.result` for it, only the top-level `checklist` definition — which is what keeps
// the subtab named, the recalculating spinner on, and the Alerts card showing "Calculating" instead
// of reading `calculatedResult: false` as Failed. `categories` stays an array because the Alerts
// table reduces over it.
const hasNoResultYet = flatResult =>
  CHECKLIST_INITIAL_STATUSES.includes(flatResult.statusType) && flatResult.id === EMPTY_GUID;

const toChecklistDefinitionHead = flatResult => ({
  id: flatResult.targetChecklistId,
  name: flatResult.name,
  isHidden: flatResult.isHidden,
  displaySettings: { statusDisplayType: flatResult.statusDisplayType },
});

const mapFlatChecklistResultToLoanSnapshot = flatResult => {
  const target = {
    checklistId: flatResult.targetChecklistId,
    targetId: flatResult.targetId,
    targetType: flatResult.targetType,
    executionType: flatResult.executionType,
  };

  if (hasNoResultYet(flatResult)) {
    return {
      target,
      status: { type: flatResult.statusType },
      checklist: { ...toChecklistDefinitionHead(flatResult), categories: [] },
    };
  }

  return {
    target,
    status: {
      type: flatResult.statusType,
      result: toChecklistResultFields(flatResult),
    },
  };
};

/**
 * Maps a `POST api/checklist-results/v2` response (`FlatChecklistResult[]`, one entry per
 * (checklist, lane)) back to the legacy `ChecklistExecutionSnapshotModel[]` shape, for a drop-in
 * swap of `getChecklistsExecutionSnapshotsAction` (the mount read) and
 * `getApplicationChecklistByIdAction` (the per-checklist FULL read) behind `ReadChecklistsFromFlatStore`.
 * @param {Array<object>} flatResults
 * @returns {Array<object>}
 */
export const mapFlatChecklistResultsToLoanSnapshots = (flatResults = []) =>
  (flatResults ?? []).map(mapFlatChecklistResultToLoanSnapshot);

/**
 * Maps a `FlatChecklistResult[]` response back to the legacy `ChecklistResultModel[]` shape — the
 * `toChecklistResultFields` half of `mapFlatChecklistResultToLoanSnapshot` above IS this shape,
 * unwrapped (no `target`/`status` envelope). `ChecklistResultModel[]` is the single-lane (no
 * Saved/PendingChanges split) legacy wire shape shared by TWO endpoints: `GET
 * api/sub-companies/{id}/checklist-result/v2` and SkynetAPI's `GET checklists/result/{applicationId}`
 * — a drop-in swap of `getSubCompanyChecklistResultsAction`'s and `getApplicationChecklistsAction`'s
 * data source behind `ReadChecklistsFromFlatStore`. Callers reading from the broad
 * `targetEntityIds`/`targetEntityType` flat filter (which returns both lanes) must pre-filter to the
 * `Saved` lane before mapping — this shape has no mergeStrategy to collapse a Saved/PendingChanges pair.
 * @param {Array<object>} flatResults
 * @returns {Array<object>}
 */
export const mapFlatChecklistResultsToChecklistResultModels = (flatResults = []) =>
  (flatResults ?? []).filter(_.negate(hasNoResultYet)).map(toChecklistResultFields);
